import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store/reducers';
import { Users, ShieldCheck, UserCheck, ArrowRight, FileText, CheckCircle, Clock, Eye } from 'lucide-react';
import { mockUsers, decisions, statutLabels, roleLabels, mockLoginLogs } from '../data/mockData';
import './AdminDashboard.css';

const AdminDashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  const displayName = user?.username || 'Admin';
  const totalUsers = mockUsers.length;
  const activeUsers = mockUsers.filter(u => u.statut === 'actif').length;
  const adminCount = mockUsers.filter(u => u.role === 'admin').length;

  const totalDecisions = decisions.length;
  const publishedCount = decisions.filter(d => d.statut === 'publie').length;
  const validatedCount = decisions.filter(d => d.statut === 'valide').length;
  const pendingCount = decisions.filter(d => d.statut === 'a_verifier').length;

  const recentUsers = [...mockUsers].sort((a, b) =>
    (b.derniereConnexion || '').localeCompare(a.derniereConnexion || '')
  ).slice(0, 5);

  const recentLogs = mockLoginLogs.slice(0, 4);

  return (
    <div className="admin-dash">
      <div className="admin-dash-welcome">
        <div>
          <h2>Bonjour, {displayName}</h2>
          <p>Bienvenue dans l'espace d'administration du CNDJ.</p>
        </div>
        <button className="admin-dash-btn" onClick={() => navigate('/espace-admin/utilisateurs')}>
          Gérer les utilisateurs <ArrowRight size={16} />
        </button>
      </div>

      <div className="admin-dash-stats">
        <div className="admin-dash-card">
          <div className="admin-dash-card-icon adci-total"><Users size={22} /></div>
          <div className="admin-dash-card-info">
            <span className="admin-dash-card-label">TOTAL UTILISATEURS</span>
            <span className="admin-dash-card-value">{totalUsers}</span>
          </div>
        </div>
        <div className="admin-dash-card">
          <div className="admin-dash-card-icon adci-active"><UserCheck size={22} /></div>
          <div className="admin-dash-card-info">
            <span className="admin-dash-card-label">ACTIFS</span>
            <span className="admin-dash-card-value">{activeUsers}</span>
          </div>
        </div>
        <div className="admin-dash-card">
          <div className="admin-dash-card-icon adci-admin"><ShieldCheck size={22} /></div>
          <div className="admin-dash-card-info">
            <span className="admin-dash-card-label">ADMINISTRATEURS</span>
            <span className="admin-dash-card-value">{adminCount}</span>
          </div>
        </div>
      </div>

      {/* Decisions overview */}
      <div className="admin-dash-decisions">
        <div className="admin-dash-section-header">
          <h3>Apercu des décisions</h3>
        </div>
        <div className="admin-dash-decision-stats">
          <div className="admin-dec-stat">
            <div className="admin-dec-stat-icon adsi-total"><FileText size={18} /></div>
            <div className="admin-dec-stat-info">
              <span className="admin-dec-stat-value">{totalDecisions}</span>
              <span className="admin-dec-stat-label">Total</span>
            </div>
          </div>
          <div className="admin-dec-stat">
            <div className="admin-dec-stat-icon adsi-published"><CheckCircle size={18} /></div>
            <div className="admin-dec-stat-info">
              <span className="admin-dec-stat-value">{publishedCount}</span>
              <span className="admin-dec-stat-label">Publiées</span>
            </div>
          </div>
          <div className="admin-dec-stat">
            <div className="admin-dec-stat-icon adsi-validated"><Eye size={18} /></div>
            <div className="admin-dec-stat-info">
              <span className="admin-dec-stat-value">{validatedCount}</span>
              <span className="admin-dec-stat-label">Validées</span>
            </div>
          </div>
          <div className="admin-dec-stat">
            <div className="admin-dec-stat-icon adsi-pending"><Clock size={18} /></div>
            <div className="admin-dec-stat-info">
              <span className="admin-dec-stat-value">{pendingCount}</span>
              <span className="admin-dec-stat-label">À vérifier</span>
            </div>
          </div>
        </div>

        <table className="admin-dec-table">
          <thead>
            <tr>
              <th>N° Décision</th>
              <th>Juridiction</th>
              <th>Nature</th>
              <th>Date</th>
              <th>Statut</th>
              <th>Agent</th>
            </tr>
          </thead>
          <tbody>
            {decisions.map(d => {
              const s = statutLabels[d.statut];
              return (
                <tr key={d.id}>
                  <td className="admin-dec-numero">{d.numero}</td>
                  <td>{d.juridiction}</td>
                  <td>{d.nature}</td>
                  <td className="admin-dec-date">{new Date(d.date).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <span className="admin-dec-badge" style={{ color: s.color, background: s.bg }}>
                      {s.label}
                    </span>
                  </td>
                  <td>{d.agent}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Two-column: Users + Recent activity */}
      <div className="admin-dash-grid">
        {/* Users overview */}
        <div className="admin-dash-panel">
          <div className="admin-dash-section-header">
            <h3>Utilisateurs</h3>
            <button className="admin-dash-link" onClick={() => navigate('/espace-admin/utilisateurs')}>
              Voir tout <ArrowRight size={14} />
            </button>
          </div>
          <div className="admin-user-list">
            {recentUsers.map(u => {
              const r = roleLabels[u.role];
              return (
                <div key={u.id} className="admin-user-row">
                  <div className="admin-user-left">
                    <div className="admin-user-avatar">{u.username.charAt(0).toUpperCase()}</div>
                    <div className="admin-user-info">
                      <span className="admin-user-name">{u.username}</span>
                      <span className="admin-user-email">{u.email}</span>
                    </div>
                  </div>
                  <div className="admin-user-right">
                    <span className="admin-user-role" style={{ color: r.color, background: r.bg }}>
                      {r.label}
                    </span>
                    <span className={`admin-user-status ${u.statut === 'actif' ? 'admin-status-active' : 'admin-status-inactive'}`}>
                      {u.statut === 'actif' ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent connexions */}
        <div className="admin-dash-panel">
          <div className="admin-dash-section-header">
            <h3>Connexions récentes</h3>
            <button className="admin-dash-link" onClick={() => navigate('/espace-admin/historique')}>
              Voir tout <ArrowRight size={14} />
            </button>
          </div>
          <div className="admin-log-list">
            {recentLogs.map(log => (
              <div key={log.id} className={`admin-log-row ${log.resultat === 'echec' ? 'admin-log-fail' : ''}`}>
                <div className="admin-log-left">
                  <div className={`admin-log-avatar ${log.resultat === 'echec' ? 'admin-log-avatar-fail' : ''}`}>
                    {log.username.charAt(0).toUpperCase()}
                  </div>
                  <div className="admin-log-info">
                    <span className="admin-log-name">{log.username}</span>
                    <span className="admin-log-meta">{new Date(log.date).toLocaleDateString('fr-FR')} · {log.heure}</span>
                  </div>
                </div>
                <span className={`admin-log-badge ${log.resultat === 'succes' ? 'admin-log-success' : 'admin-log-echec'}`}>
                  {log.resultat === 'succes' ? 'Succès' : 'Échec'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
